import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, BookOpen, Play, Star, Zap, ChevronRight
} from "lucide-react";

/**
 * Banking English Language Practice Hub
 */
const BankingEnglishPage = () => {
  const navigate = useNavigate();

  const topics = [
    {
      id: 'reading-comprehension',
      name: 'Reading Comprehension',
      description: '3-4 passages, inference questions',
      questions: 150,
      practiced: 0,
      difficulty: 'Medium',
      icon: '📖',
      color: 'bg-blue-500'
    },
    {
      id: 'cloze-test',
      name: 'Cloze Test',
      description: 'Fill in the blanks in paragraph',
      questions: 100,
      practiced: 0,
      difficulty: 'Medium',
      icon: '📝',
      color: 'bg-indigo-500'
    },
    {
      id: 'error-spotting',
      name: 'Error Spotting',
      description: 'Find grammatical errors',
      questions: 120,
      practiced: 0,
      difficulty: 'Easy',
      icon: '🔍',
      color: 'bg-emerald-500'
    },
    {
      id: 'sentence-improvement',
      name: 'Sentence Improvement',
      description: 'Replace the underlined part',
      questions: 80,
      practiced: 0,
      difficulty: 'Medium',
      icon: '✨',
      color: 'bg-purple-500'
    },
    {
      id: 'para-jumbles',
      name: 'Para Jumbles',
      description: 'Rearrange sentences',
      questions: 70,
      practiced: 0,
      difficulty: 'Medium',
      icon: '🔀',
      color: 'bg-amber-500'
    },
    {
      id: 'fill-blanks',
      name: 'Fill in the Blanks',
      description: 'Single & double fillers',
      questions: 100,
      practiced: 0,
      difficulty: 'Easy',
      icon: '➖',
      color: 'bg-teal-500'
    },
    {
      id: 'vocabulary',
      name: 'Vocabulary',
      description: 'Synonyms, antonyms, idioms',
      questions: 200,
      practiced: 0,
      difficulty: 'Easy',
      icon: '📚',
      color: 'bg-pink-500'
    },
    {
      id: 'sentence-rearrangement',
      name: 'Sentence Rearrangement',
      description: 'Arrange in logical order',
      questions: 60,
      practiced: 0,
      difficulty: 'Medium',
      icon: '📋',
      color: 'bg-rose-500'
    }
  ];

  const getDifficultyColor = (diff) => {
    switch(diff) {
      case 'Easy': return 'bg-emerald-100 text-emerald-700';
      case 'Medium': return 'bg-amber-100 text-amber-700';
      case 'High': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/banking')}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">English Language</h1>
                <p className="text-blue-200 text-sm">30 Marks in Prelims | 40 Marks in Mains</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-blue-200">Questions Done</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0%</p>
              <p className="text-xs text-blue-200">Accuracy</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-blue-200">RC Passages</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">8</p>
              <p className="text-xs text-blue-200">Topics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Practice */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-400" />
                  Quick Practice Session
                </h3>
                <p className="text-slate-400 text-sm">15 questions - RC + Grammar + Vocabulary</p>
              </div>
              <Button 
                className="bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => navigate('/banking/english/practice?section=english&mode=mixed')}
              >
                <Play className="w-4 h-4 mr-2" /> Start Practice
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Topics Grid */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Practice by Topic</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {topics.map((topic) => (
              <Card
                key={topic.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 border-transparent hover:border-blue-200"
                onClick={() => navigate(`/banking/english/practice?section=english&topic=${topic.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 ${topic.color} rounded-lg flex items-center justify-center`}>
                      <span className="text-xl">{topic.icon}</span>
                    </div>
                    <Badge className={getDifficultyColor(topic.difficulty)}>
                      {topic.difficulty}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">{topic.name}</h3>
                  <p className="text-xs text-slate-500 mb-3">{topic.description}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{topic.practiced}/{topic.questions} done</span>
                    <span>{Math.round((topic.practiced/topic.questions)*100)}%</span>
                  </div>
                  <Progress value={(topic.practiced/topic.questions)*100} className="h-1.5 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" /> English Tips for Banking Exams
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>- RC = 10-12 marks. Read the passage quickly, focus on questions</li>
              <li>- Learn 20 new words daily - most vocabulary is repeated</li>
              <li>- For Error Spotting: Subject-Verb agreement, Tenses, Prepositions</li>
              <li>- Cloze Test: Read the entire passage first, then fill blanks</li>
              <li>- Don't skip English - it's the easiest section to score 25+</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankingEnglishPage;
